<template>
  <div>
    <div v-if="isAlgoliaReady">
      <ais-instant-search :search-client="algoliaClient" :index-name="algoliaIndexName">
        <ais-index ref="aisIndex" :index-name="algoliaIndexName"/>
        <ais-configure :hits-per-page.camel="10" :disjunctive-facets-refinements.camel="disjunctiveFacetsRefinements" />
        <v-layout wrap>
          <v-flex xs12 text-end>
            <ais-stats>
              <p slot-scope="{ nbHits }">
                <small class="mr-3">{{ nbHits }} {{ nbHits >= 2 ? $t('aplural') : $t('a') }}</small>
                <span id="algoliaRefreshContainer"><algolia-refresh/></span>
              </p>
            </ais-stats>
          </v-flex>
        </v-layout>
        <v-layout wrap>
          <v-flex xs3>
            <v-switch dense color="secondary" inset v-model="isShowingAllDossiersOfTheCompany" class="mt-1">
              <template v-slot:label>
                <small class="black--text" style="margin-top: -2px">{{ $t('b') }}</small>
              </template>
            </v-switch>
          </v-flex>
          <v-flex xs9 pl-12>
            <div>
              <ais-search-box :placeholder="$t('c')"/>
              <ais-search-box v-show="false" :value="urlParamQuery" />
            </div>
          </v-flex>
        </v-layout>
        <v-layout wrap mt-4>
          <v-flex xs3>
            <algolia-filters/>
          </v-flex>
          <v-flex xs9 pl-12>
            <v-layout wrap>
              <v-flex xs12 mb-2>
                <ais-clear-refinements :excluded-attributes="['userId', 'companyId', 'sharingMode', 'id']">
                  <div slot-scope="{ canRefine, refine }">
                    <v-btn v-if="canRefine" x-small outlined @click="refine">
                      <v-icon x-small class="mr-2">mdi-close</v-icon>
                      {{ $t('clearFilters') }}
                    </v-btn>
                  </div>
                </ais-clear-refinements>
              </v-flex>
            </v-layout>
            <v-layout wrap>
              <v-flex xs12 mb-4>
                <ais-current-refinements :excluded-attributes="['companyId', 'sharingMode']" :transform-items="transformItems">
                  <div slot-scope="{ items }">
                    <v-layout wrap v-for="(item, key) in items.filter(item => item.attribute !== 'userId')" :key="key">
                      <v-flex xs12>
                        <b><small class="mr-1">{{ item.label }} :</small></b>
                        <span v-for="(refinement, key) in item.refinements" :key="key">
                          <v-chip
                            small
                            class="ml-2 mb-1"
                            close
                            @click:close="removeCurrentRefinement(item, refinement)"
                          >
                            {{ refinement.label }}
                          </v-chip>
                        </span>
                      </v-flex>
                    </v-layout>
                  </div>
                </ais-current-refinements>
              </v-flex>
            </v-layout>
            <v-layout wrap>
              <v-flex xs12 v-if="user.company && user.company.isListDossiersDisabled">
                <v-layout wrap>
                  <v-flex xs12 text-center>
                    <div><img :src="images.files" height="180px" class=""></div>
                  </v-flex>
                </v-layout>
                <v-layout wrap justify-center>
                  <v-flex xs7 text-center>
                    <v-card flat outlined class="px-4 py-3">
                      <ais-stats>
                        <div slot-scope="{ nbHits }">
                          <div>{{ $t('listDossiersDisabled1a') }} {{ nbHits }} {{ $t('listDossiersDisabled1b') }}</div>
                          <div><b>{{ $t('listDossiersDisabled2') }}</b></div>
                        </div>
                      </ais-stats>
                    </v-card>
                  </v-flex>
                </v-layout>
              </v-flex>
              <v-flex xs12 v-else>
                <ais-hits>
                  <div slot-scope="{ items,}">
                    <div v-for="item in items" :key="item.objectID" class="mb-6">
                      <dossier :dossier="item" :isShowingAllDossiersOfTheCompany="isShowingAllDossiersOfTheCompany" @updateAlgoliaResults="updateAlgoliaResults" @goToExistingDossierOfConsultant="goToExistingDossierOfConsultant"/>
                    </div>
                  </div>
                </ais-hits>
              </v-flex>
            </v-layout>
            <v-layout wrap justify-center v-if="!(user.company && user.company.isListDossiersDisabled)">
              <v-flex xs12>
                <ais-state-results>
                  <template v-slot="{results: { hits } }">
                    <ais-pagination v-if="hits.length > 0" class="mt-8"/>
                    <div v-else>
                      <v-layout wrap mt-12 pt-12>
                        <v-flex xs7 mb-12 text-center>
                          <div>
                            <small>{{ $t('noResults') }}</small>
                          </div>
                          <v-btn to="/dossiers/create" class="secondary mt-1" v-if="user.company.dossiersLeftNb > 0">
                            <v-icon class="mr-1" small>mdi-plus</v-icon>{{ $t('10') }}
                          </v-btn>
                        </v-flex>
                        <v-spacer/>
                      </v-layout>
                    </div>
                  </template>
                </ais-state-results>
              </v-flex>
            </v-layout>
          </v-flex>
        </v-layout>
      </ais-instant-search>
    </div>
    <div v-else>
      <v-layout wrap justify-center>
        <v-flex xs6>
          <v-progress-linear indeterminate color="primary" class="mt-12"></v-progress-linear>
        </v-flex>
      </v-layout>
    </div>
  </div>
</template>

<script>
import store from "@/store";
import { mapGetters } from "vuex";
import algoliasearch from 'algoliasearch';
import 'instantsearch.css/themes/satellite-min.css';
import Dossier from '@/components/Dossier'
import AlgoliaFilters from '@/components/algolia/AlgoliaFilters'
import AlgoliaRefresh from '@/components/algolia/AlgoliaRefresh';

const ALGOLIA_APP_ID = 'GDWN6HP8CM'
const ALGOLIA_INDEX_NAME = process.env.VUE_APP_ENV === 'production' ? 'prod_dossiers' : 'dev_dossiers'
const allLanguages = require('@/assets/app/data/languages.json')

export default {
  components: {
    Dossier,
    AlgoliaFilters,
    AlgoliaRefresh
  },
  data: () => ({
    algoliaIndexName: ALGOLIA_INDEX_NAME,
    algoliaClient: null,
    hits: [],
    isAlgoliaReady: false,
    searchStore: null,
    isShowingAllDossiersOfTheCompany: false,
    idOfTheOnlyDossierToShow: null,
    algoliaRefreshTimers: [],
    images: {
      files: require('@/assets/app/images/data_arranging_outline.svg')
    },
  }),
  async mounted () {
    if (this.$route.query.isShowingAllDossiersOfTheCompany === '1') this.isShowingAllDossiersOfTheCompany = true
    if (this.$route.query.dossierId) this.idOfTheOnlyDossierToShow = this.$route.query.dossierId
    await this.initAlgolia()
  },
  beforeDestroy () {
    // Stop the pending Algolia refreshes: without this they keep firing (and keep
    // costing search operations) after the user has left the search page.
    this.algoliaRefreshTimers.forEach(clearTimeout)
    this.algoliaRefreshTimers = []
  },
  computed: {
    ...mapGetters({
      user: "user"
    }),
    disjunctiveFacetsRefinements() {
      return {
        userId: this.isShowingAllDossiersOfTheCompany ? [] : [this.$auth.currentUser.uid],
        id: this.idOfTheOnlyDossierToShow ? [this.idOfTheOnlyDossierToShow] : []
      }
    },
    urlParamQuery() {
      if (this.$route.query.q) return this.$route.query.q.replace(/"/g, '')
      else return ''
    }
  },
  methods: {
    async initAlgolia() {
      const algoliaSecuredKey = await this.getAlgoliaSecuredKey()
      this.algoliaClient = algoliasearch(ALGOLIA_APP_ID, algoliaSecuredKey);
      await this.regularlyUpdateAlgoliaResults()
      this.isAlgoliaReady = true
    },
    async getAlgoliaSecuredKey() {
      if (this.user.algoliaSecuredKey) {
        return this.user.algoliaSecuredKey
      } else {
        const algoliaSecuredKey = (await this.$functions.httpsCallable('getAlgoliaSecuredApikey')()).data.algoliaSecuredKey
        store.dispatch('setAlgoliaSecuredKey', algoliaSecuredKey)
        return algoliaSecuredKey
      }
    },
    async updateAlgoliaResultsOnce() {
      // console.log('Refreshing Algolia'); // eslint-disable-line
      this.algoliaClient.clearCache()
      const algoliaRefreshButtonElt = document.querySelector('#algoliaRefreshContainer > button')
      if (algoliaRefreshButtonElt) algoliaRefreshButtonElt.click()
    },
    regularlyUpdateAlgoliaResults() {
      this.updateAlgoliaResultsOnce()
      for (let timeInMs of [2000, 5000, 12000, 30000]) {
        this.algoliaRefreshTimers.push(setTimeout(() => {
          if (document.querySelector('.ais-RefinementList-searchBox input') !== document.activeElement) {
            this.updateAlgoliaResultsOnce()
          }
        }, timeInMs))
      }
    },
    async updateAlgoliaResults(){
      await this.updateAlgoliaResultsOnce()
    },
    transformItems(items){
      return items.map(item => {
        item.label = {
          "profile.skills.technicalSkillsWithText.skillNames": this.$t('filterNameA'),
          "profile.xpYears": this.$t('filterNameB'),
          "profile.languages.code": this.$t('filterNameC'),
          "query": this.$t('filterNameD'),
          "labelIds": this.$t('filterNameE'),
          "id": this.$t('filterNameF')
        }[item.attribute]
        if (item.attribute === 'profile.languages.code') {
          item.refinements = item.refinements.map(refinement => {
            refinement.label = this.getLanguageName(refinement.label)
            return refinement
          })
        }
        if (item.attribute === 'labelIds') {
          item.refinements = item.refinements.map(refinement => {
            refinement.label = this.getLabelName(refinement.label)
            return refinement
          })
        }
        return item
      })
    },
    getLanguageName(languageCode) {
      const interfaceLanguage = this.user.data.locale // 'fr' or 'en'
      return allLanguages.find(l => l.iso_code === languageCode).descriptions[interfaceLanguage]
    },
    getLabelName(labelId) {
      return this.user.company.labels.find(label => label.id === labelId).name
    },
    goToExistingDossierOfConsultant(data) {
      window.scrollTo(0, 0)
      this.$router.push(`/dossiers`)
      setTimeout(() => {
        this.isShowingAllDossiersOfTheCompany = true
        this.$router.push(`/dossiers?isShowingAllDossiersOfTheCompany=1&q=${encodeURIComponent(data.emailAddressOfConsultantToInvite)}`)
      },1)
    },
    removeCurrentRefinement(item, refinement) {  // eslint-disable-line
      if (item.attribute === 'id') {
        this.idOfTheOnlyDossierToShow = null
      } else {
        item.refine(refinement)
      }
    }
  },
};
</script>

<style>
.my-ais-ToggleRefinement-count {
  display: none !important;
}
</style>


<i18n>
{
  "fr": {
    "a": "dossier trouvé.",
    "aplural": "dossiers trouvés.",
    "b": "Dossiers de toute l'équipe",
    "c": "Rechercher dans les dossiers...",
    "1": "Passez à la formule",
    "2": "Standard",
    "3": "pour centraliser les dossiers de plusieurs utilisateurs.",
    "4": "Passez à la formule",
    "5": "Standard",
    "6": "pour activer les filtres.",
    "7": "Passez à la formule",
    "8": "Standard",
    "9": "pour activer la recherche par mots-clefs.",
    "10": "Créez un nouveau dossier",
    "filterNameA": "Technologies",
    "filterNameB": "Années d'expériences",
    "filterNameC": "Langues",
    "filterNameD": "Mots-clefs",
    "filterNameE": "Labels",
    "filterNameF": "Identifiant du dossier",
    "clearFilters": "Réinitialiser les filtres",
    "noResults": "Pas de dossiers trouvés.",
    "listDossiersDisabled1a": "L'accès à vos",
    "listDossiersDisabled1b": "dossiers est bloqué.",
    "listDossiersDisabled2": "Reprenez un abonnement pour pouvoir à nouveau créer et télécharger vos dossiers."
  },
  "en": {
    "a": "CV found.",
    "aplural": "CVs found.",
    "b": "CVs of the entire team",
    "c": "Search CVs...",
    "1": "Upgrade your plan to",
    "2": "Standard",
    "3": "to share CVs among users of your team.",
    "4": "Upgrade your plan to",
    "5": "Standard",
    "6": "to enable filters.",
    "7": "Upgrade your plan to",
    "8": "Standard",
    "9": "to enable search.",
    "10": "Transform a new CV",
    "filterNameA": "Technologies",
    "filterNameB": "Years of experience",
    "filterNameC": "Languages",
    "filterNameD": "Query",
    "filterNameE": "Labels",
    "filterNameF": "CV id",
    "clearFilters": "Clear filters",
    "noResults": "No CV found.",
    "listDossiersDisabled1a": "Access to your",
    "listDossiersDisabled1b": "CVs is blocked.",
    "listDossiersDisabled2": "Re-subscribe to be able to transform and download your CVs."
  }
}
</i18n>